<template>
    <div
        v-if="computedLocales.length > 1"
        v-click-outside="close"
        class="language"
        :class="{
            [`theme-${theme}`]: true,
            'open': isOpen,
        }"
    >
        <button class="label" @click="isOpen = isOpen ? false : true">
            <span>{{ getLanguage($i18n.localeProperties.code) }}</span>
            <DSIcon code="chevron_filled_down" />
        </button>
        <div
            class="options"
        >
            <nuxt-link
                v-for="(locale, key) in computedLocales"
                :key="`${key}-${locale.label}`"
                :to="locale.link"
                class="option"
                :aria-label="$t('navigation.selectedLanguageHelper', { language: $t(`navigation.${locale.language}`) })"
                :class="{
                    'selected': $i18n.localeProperties.code === locale.label,
                }"
            >
                {{ getLanguage(locale.label) }}
            </nuxt-link>
        </div>
    </div>
</template>
<script setup>
import { ref, computed, inject, watch, useI18n, useRoute } from '#imports';
import vClickOutside from '~dsc/directives/click-outside';
import { usePageStore } from '~dsc/stores/cms/page';
import { storeToRefs } from 'pinia';

const theme = inject('theme');
const i18n = useI18n();
const isOpen = ref(false);
const route = useRoute();

const computedLocales = computed(() => i18n.locales.value.map(item => {
    const { localizedPagePaths } = storeToRefs(usePageStore());
    const values = [];
    if (item.code !== i18n.defaultLocale) {
        values.push(item.code);
    }
    if (localizedPagePaths && localizedPagePaths[item.code] && localizedPagePaths[item.code] !== '/') {
        values.push(getLocalizedPagePaths[item.code]);
    }

    return {
        label: item.code,
        link: `/${values.join('/')}`,
        language: item.name,
    };
}));
const getLanguage = locale => {
    const { 0: language } = locale.split('-');

    return language;
};
const close = () => {
    isOpen.value = false;
};
watch(() => route.path, () => {
    isOpen.value = false;
});
</script>
<style scoped>
.language {
  position: relative;
}
.language.theme-black .label {
  color: var(--color-base-white);
}
.language.theme-black .option {
  color: var(--color-base-white);
}
.language.theme-black .option.selected {
  color: var(--color-secondary-500);
  border-bottom: 0.25rem solid var(--color-secondary-500);
}
.language .label {
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  padding: 0;
  min-height: 1.5rem;
  min-width: 35px;
  justify-content: flex-end;
  font-weight: 700;
}
.language .label .icon {
  transition: all 0.2s ease-in-out;
  position: relative;
  max-width: 0.5rem;
  margin-left: 0.25rem;
  transform: rotate(0) translateY(0);
  transform-origin: center center;
}
.language .label:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}
.language .options {
  transition: all 0.2s ease-in-out;
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: flex-start;
  flex-flow: row wrap;
  transform: translateY(100%) scaleY(0);
  transform-origin: center top;
  background: var(--color-base-white);
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  box-shadow: var(--panel-box-shadow);
  z-index: 1;
}
.language .options .option {
  transition: all 0.2s ease-in-out;
  line-height: 1.5em;
  color: var(--color-base-black);
  font-weight: 400;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}
.language .options .option.selected, .language .options .option:hover {
  color: var(--color-base-black);
  font-weight: 800;
}
.language .options .option.selected {
  pointer-events: none;
  border-bottom: 0.25rem solid var(--color-primary-500);
}
.language .options .option:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}
.language.open .label .icon {
  transform: rotate(-180deg) translateY(2px);
}
.language.open .options {
  transform: translateY(100%) scaleY(1);
}
@media (max-width: 1199px) {
  .language .label {
    display: none;
  }
  .language .options {
    position: static;
    opacity: 1;
    transform: scale(1);
    background: none;
    padding: 0;
    box-shadow: none;
  }
  .language .options .option:not(:last-child) {
    margin-right: 1rem;
  }
}
</style>
